import { useEffect, useState } from "react";
import { useI18n } from "../../i18n";
import { saveSettings } from "../../lib/storage";
import type { AppSettings } from "../../types";

export function Settings({
  settings,
  onChange,
}: {
  settings: AppSettings;
  onChange: (settings: AppSettings) => void;
}): React.JSX.Element {
  const { language, t } = useI18n();
  const [draft, setDraft] = useState(settings);
  const [showKey, setShowKey] = useState(false);
  const [notice, setNotice] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  useEffect(() => setDraft(settings), [settings]);
  const provider = draft.provider;
  const updateProvider = (patch: Partial<AppSettings["provider"]>): void =>
    setDraft((current) => ({
      ...current,
      provider: { ...current.provider, ...patch },
    }));
  const save = (): void => {
    const next = { ...draft, language };
    saveSettings(next);
    onChange(next);
    setNotice({ kind: "success", text: t("settings.saved") });
  };
  return (
    <div className="settings-grid">
      <section className="panel settings-form">
        <div className="panel-heading">
          <div>
            <small>BYOK</small>
            <h2>{t("settings.provider")}</h2>
          </div>
        </div>
        <p className="notice info">{t("settings.comingSoon")}</p>
        <label className="toggle-row">
          <input
            type="checkbox"
            disabled
            checked={provider.enabled}
            onChange={(event) =>
              updateProvider({ enabled: event.target.checked })
            }
          />
          <span>
            <strong>{t("settings.enable")}</strong>
            <small>{t("settings.enableHelp")}</small>
          </span>
        </label>
        <label className="field">
          <span>{t("settings.baseUrl")}</span>
          <input
            type="url"
            disabled
            value={provider.baseUrl}
            onChange={(event) =>
              updateProvider({ baseUrl: event.target.value })
            }
            placeholder="https://api.openai.com/v1"
          />
        </label>
        <label className="field">
          <span>{t("settings.model")}</span>
          <input
            disabled
            value={provider.model}
            onChange={(event) => updateProvider({ model: event.target.value })}
            placeholder="gpt-4.1-mini"
          />
        </label>
        <label className="field">
          <span>{t("settings.apiKey")}</span>
          <div className="input-action">
            <input
              type={showKey ? "text" : "password"}
              disabled
              autoComplete="off"
              value={provider.apiKey}
              onChange={(event) =>
                updateProvider({ apiKey: event.target.value })
              }
              placeholder={t("settings.apiKeyPlaceholder")}
            />
            <button
              type="button"
              disabled
              onClick={() => setShowKey((value) => !value)}
            >
              {showKey ? t("settings.hideKey") : t("settings.showKey")}
            </button>
          </div>
        </label>
        <div className="field">
          <span>{t("settings.theme")}</span>
          <div className="segmented">
            {(["light", "dark", "system"] as const).map((theme) => (
              <button
                type="button"
                className={draft.theme === theme ? "active" : ""}
                key={theme}
                onClick={() => setDraft((current) => ({ ...current, theme }))}
              >
                {t(`settings.${theme}`)}
              </button>
            ))}
          </div>
        </div>
        {notice && <p className={`notice ${notice.kind}`}>{notice.text}</p>}
        <div className="form-actions split-actions">
          <button
            className="text-button danger-text"
            type="button"
            disabled
            onClick={() => updateProvider({ apiKey: "" })}
          >
            {t("settings.clearKey")}
          </button>
          <div>
            <button className="secondary" type="button" disabled>
              {t("settings.test")}
            </button>
            <button className="primary" type="button" onClick={save}>
              {t("settings.save")}
            </button>
          </div>
        </div>
      </section>
      <aside className="panel security-note">
        <div className="note-icon">!</div>
        <h2>{t("settings.securityTitle")}</h2>
        <p>{t("settings.securityBody")}</p>
        <p>{t("settings.cors")}</p>
      </aside>
    </div>
  );
}
